import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { SessionProvider } from './lib/session';
import { hasCsrfCookie, onPageShow } from './lib/sign-out';
import { ToastProvider } from './components/Toast';
/*
 * The three faces, bundled from this origin — the content-security-policy is
 * `font-src 'self'`. Variable files with unicode ranges, so a browser fetches
 * only the Latin file it needs. Archivo includes its width axis, which the
 * expanded headings use. See `fontFamily` in tailwind.config.js.
 */
import '@fontsource-variable/inter/wght.css';
import '@fontsource-variable/archivo/wdth.css';
import '@fontsource-variable/jetbrains-mono/wght.css';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5_000 },
  },
});

/**
 * Registered only for the technician surface. The dispatcher console is used at
 * a desk with a connection, and a stale cached console is a support call nobody
 * wants — a technician's phone in a cabinet is the case that needs it.
 */
if ('serviceWorker' in navigator) {
  const onTechnicianPage = window.location.pathname.startsWith('/t/');

  window.addEventListener('load', () => {
    if (onTechnicianPage) {
      /*
       * Scoped to /t/, which is the whole point.
       *
       * A worker's default scope is the directory it is served from, so this
       * one — living at the root — controlled the entire origin, console
       * included. nginx sends `Service-Worker-Allowed: /t/` so it can live at
       * the root and own only the technician surface.
       */
      void navigator.serviceWorker.register('/sw.js', { scope: '/t/' }).catch((error: Error) => {
        // Not fatal: without it the page simply needs a connection, as before.
        console.warn('offline support unavailable:', error.message);
      });
      return;
    }

    /*
     * Anywhere else: tear down anything that still claims this page.
     *
     * Every account that had ever opened a job link carried a worker scoped to
     * the whole origin, answering the console's asset requests from a cache
     * with no fallback — and one failed fetch there is a white page with the
     * right title in the tab. Those installations cannot be reached to be
     * told; they have to repair themselves the next time the page loads at
     * all.
     *
     * The reload is guarded by a session flag. Without it, a browser that
     * re-registers for any reason would reload on every visit, which is a
     * worse fault than the one being fixed.
     */
    void navigator.serviceWorker.getRegistrations().then(async (registrations) => {
      const originWide = registrations.filter(
        (registration) => !new URL(registration.scope).pathname.startsWith('/t/'),
      );
      if (originWide.length === 0) return;

      await Promise.all(originWide.map((registration) => registration.unregister()));

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }

      const HEALED = 'gsn-sw-healed';
      if (navigator.serviceWorker.controller && !sessionStorage.getItem(HEALED)) {
        sessionStorage.setItem(HEALED, '1');
        window.location.reload();
      }
    });
  });
}

/*
 * Back to a page the browser kept in memory (ADR-038).
 *
 * The back/forward cache restores a page exactly as it was left, without
 * asking the server anything — after a sign-out, that would be the console
 * with somebody's tickets on it. This hides the page before it is painted and
 * decides from the session, not from the copy. Registered before anything
 * renders so it is in place for the very first restore.
 */
window.addEventListener('pageshow', (event) => {
  void onPageShow(event, {
    pathname: window.location.pathname,
    hasSessionCookie: () => hasCsrfCookie(document.cookie),
    hide: () => {
      document.documentElement.style.visibility = 'hidden';
    },
    show: () => {
      document.documentElement.style.visibility = '';
    },
    refresh: () => window.location.reload(),
    stillSignedIn: async () => {
      try {
        const response = await fetch('/api/v1/auth/me', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (response.ok) return 'yes';
        // The server's own answer that this browser holds no session.
        if (response.status === 401) return 'no';
        return 'unknown';
      } catch {
        return 'unknown';
      }
    },
    leave: () => window.location.replace('/login'),
  });
});

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          {/*
            Inside the router and the session, so a toast can navigate and can
            name the person it is talking to.
          */}
          <ToastProvider>
            <App />
          </ToastProvider>
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
